import React, { useState, useRef, useEffect } from 'react';
import { X, MessageCircle, CheckCircle } from 'lucide-react';
import dmService from '../services/dmService';
import tokenService from '../services/tokenService';
import '../styles/UserProfileCard.css';

export default function UserProfileCard({ userId, userName, triggerElement, isGuestMode = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const triggerRef = useRef(triggerElement);
  const [dmSettings, setDmSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  // 獲取用戶名的第一個字
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  useEffect(() => {
    if (isOpen && !isGuestMode) {
      checkDmSettings();
    }
  }, [isOpen, userId, isGuestMode]);

  const checkDmSettings = async () => {
    try {
      const settings = await dmService.getDmSettings(userId);
      setDmSettings(settings);
    } catch (error) {
      console.error('Error checking DM settings:', error);
      setDmSettings(null);
    }
  };

  const handleApplyDm = async () => {
    // ✅ 访客模式检查
    if (isGuestMode) {
      setMessage('訪客不可私訊，請登入後使用');
      setMessageType('error');
      return;
    }
    
    try {
      setLoading(true);
      setMessage('');
      
      const result = await dmService.applyForDm(userId);
      
      setApplied(true);
      setMessage('已成功申請私訊！您可以在活動列表中查看對話');
      setMessageType('success');
      
      // 更新 token
      await tokenService.renewToken();
      
      // 發送自定義事件通知活動列表刷新
      window.dispatchEvent(new CustomEvent('dmApplicationSuccess'));
      
      // 2秒後關閉卡片
      setTimeout(() => {
        setIsOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Error applying for DM:', error);
      
      if (error.response?.status === 400) {
        setMessage('無法申請自己的私訊');
      } else if (error.response?.status === 403) {
        setMessage('此用戶未開啟私訊功能');
      } else if (error.response?.status === 200) {
        setApplied(true);
        setMessage('私訊對話已存在');
        setMessageType('success');
      } else {
        setMessage('申請失敗，請稍後再試');
      }
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target) && 
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleOpen = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const cardWidth = 360; // 預估卡片寬度
    const cardHeight = 250; // 預估卡片高度

    let top = rect.bottom + 8;
    let left = rect.left;

    // 如果右側空間不足，向左調整
    if (left + cardWidth > windowWidth - 20) {
      left = windowWidth - cardWidth - 20;
    }

    // 如果下方空間不足，顯示在上方
    if (top + cardHeight > windowHeight - 20) {
      top = rect.top - cardHeight - 8;
    }

    setPosition({ top, left });
    setIsOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        className="user-profile-trigger"
        onClick={handleOpen}
        title={`查看 ${userName} 的資料`}
      >
        <div className="user-avatar-placeholder">
          {getInitial(userName)}
        </div>
        <span className="user-name">{userName}</span>
      </button>

      {isOpen && (
        <div
          ref={cardRef}
          className="user-profile-card"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999
          }}
        >
          <div className="profile-card-header">
            <div className="profile-header-content">
              <div className="profile-avatar">
                {getInitial(userName)}
              </div>
              <div className="profile-info">
                <h3 className="profile-name">{userName}</h3>
              </div>
            </div>
            <button
              className="profile-close-btn"
              onClick={() => setIsOpen(false)}
              title="關閉"
            >
              <X size={18} />
            </button>
          </div>

          <div className="profile-card-body">
            <div className="profile-section-title">私訊</div>
            
            {isGuestMode ? (
              <div className="dm-status-message disabled">
                🔒 訪客不可私訊
              </div>
            ) : !dmSettings ? (
              <div className="dm-status-message loading">檢查私訊狀態...</div>
            ) : !dmSettings.dmOpen ? (
              <div className="dm-status-message disabled">
                🔒 該用戶未開放私訊功能
              </div>
            ) : applied ? (
              <div className="dm-status-message success">
                <CheckCircle size={20} />
                <span>已申請私訊</span>
              </div>
            ) : (
              <button
                className="apply-dm-button"
                onClick={handleApplyDm}
                disabled={loading}
              >
                <MessageCircle size={18} />
                <span>{loading ? '申請中...' : '申請私訊'}</span>
              </button>
            )}
            
            {message && (
              <div className={`dm-message ${messageType}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
